import React, { useState, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { defineMessage } from "@lingui/macro";

import { i18n } from "base/i18n";

import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE,
  preloadIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { Popover, POPOVER_POSITIONS } from "../popover/index.jsx";
import { Button, BUTTON_SIZES, BUTTON_TYPES } from "@widgets/inputs/index.jsx";

import styles from "./popupconfirm.css";

function PopupConfirm(props) {
  const {
    targetRenderer,
    onOK = () => {},
    onCancel = () => {},
    okText,
    cancelText,
    content,
    position = POPOVER_POSITIONS.BOTTOM_LEFT,
    isOpen = false,
    showButtons = true
  } = props ?? {};

  useEffect(function onLoad() {
    preloadIcon(KF_ICONS.CLOSE, ICON_SIZE.SIZE_20, ICON_STROKE.REGULAR);
  }, []);

  const [isVisibile, setIsVisible] = useState(isOpen);

  const close = () => {
    setIsVisible(false);
  };

  const cancel = () => {
    setIsVisible(false);
    onCancel();
  };

  const onOpen = () => {
    setIsVisible(true);
  };

  return (
    <Popover
      targetRenderer={targetRenderer}
      position={position}
      isOpen={isVisibile}
      onOpen={onOpen}
    >
      <div className={styles.content}>
        <div
          className={`${styles.contentContainer} ${
            showButtons ? styles.contentContainerWithButton : ""
          }`}
        >
          <div>{content}</div>
          <div className={styles.closeButton} onClick={close}>
            <KfIcon
              icon={KF_ICONS.CLOSE}
              size={ICON_SIZE.SIZE_12}
              stroke={ICON_STROKE.REGULAR}
            />
          </div>
        </div>
        {showButtons && (
          <div className={styles.buttonContainer}>
            <div className={styles.cancelButton}>
              <Button
                onClick={cancel}
                size={BUTTON_SIZES.SMALL_DEPRECATED}
                type={BUTTON_TYPES.SECONDARY}
              >
                {i18n._(cancelText)}
              </Button>
            </div>
            <div>
              <Button onClick={onOK} size={BUTTON_SIZES.SMALL_DEPRECATED}>
                {i18n._(okText)}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Popover>
  );
}

PopupConfirm.propTypes = {
  /** A callback function that should return the element which when clicked will open this popup confirm component. */
  targetRenderer: PropTypes.func,
  /** A callback function which gets invoked when the user clicks on the 'Ok' button. */
  onOK: PropTypes.func,
  /** A callback function which gets invoked when the user clicks on the 'Cancel' button. */
  onCancel: PropTypes.func,
  /** The text that should be shown inside the ok button. */
  okText: PropTypes.string,
  /** The text that should be shown inside the cancel button. */
  cancelText: PropTypes.string,
  /** The text that needs to be display inside of the popup confirm. */
  content: PropTypes.string,
  /** The direction of nose + the position of the popover. */
  position: PropTypes.oneOf(Object.values(POPOVER_POSITIONS)),
  /** Is the popup showing or not showing. */
  isOpen: PropTypes.bool,
  /** Show buttons? */
  showButtons: PropTypes.bool
};

PopupConfirm.defaultProps = {
  onOK: () => {},
  onCancel: () => {},
  okText: defineMessage`Reset`,
  cancelText: defineMessage`Cancel`,
  position: POPOVER_POSITIONS.BOTTOM_LEFT,
  isOpen: false,
  showButtons: true
};

export { PopupConfirm };
